import { createClient, SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'
import { makeFailoverFetch } from './supabaseFailoverFetch'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

// Public Cloudflare Worker fallback route (not a secret). Env-overridable.
const SUPABASE_PROXY_URL =
  import.meta.env.VITE_SUPABASE_PROXY_URL ?? 'https://ghp-supabase-proxy.izaa.workers.dev'
const PROXY_ORIGINS = SUPABASE_PROXY_URL ? [SUPABASE_PROXY_URL] : []

// Singleton pattern to prevent multiple clients during HMR
declare global {
  interface Window {
    __supabaseClient?: SupabaseClient<Database>
    __supabaseInitialized?: boolean
  }
}

function getSupabaseClient(): SupabaseClient<Database> {
  // Reuse existing client if available (prevents HMR issues)
  if (typeof window !== 'undefined' && window.__supabaseClient && window.__supabaseInitialized) {
    return window.__supabaseClient
  }

  const client = createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: false,
      storageKey: 'ghp-dashboard-auth',
      // CRITICAL: Bypass Web Locks API to prevent getSession() from deadlocking.
      lock: async (_name: string, _acquireTimeout: number, fn: () => Promise<any>) => {
        return await fn()
      },
    },
    global: {
      fetch: makeFailoverFetch({ directOrigin: supabaseUrl, proxyOrigins: PROXY_ORIGINS }),
    },
  })

  // Store on window for HMR survival
  if (typeof window !== 'undefined') {
    window.__supabaseClient = client
    window.__supabaseInitialized = true
    console.log('[Supabase] Client initialized')
  }

  return client
}

export const supabase = getSupabaseClient()

// Helper to get current user
export const getCurrentUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser()
  if (error) throw error
  return user
}

// Helper to get current session
export const getCurrentSession = async () => {
  const { data: { session }, error } = await supabase.auth.getSession()
  if (error) throw error
  return session
}

// Sign in with email and password
export const signIn = async (email: string, password: string) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  })
  if (error) throw error
  return data
}

// Sign out
export const signOut = async () => {
  const { error } = await supabase.auth.signOut()
  if (error) throw error
}

// Listen to auth state changes
export const onAuthStateChange = (callback: (event: string, session: any) => void) => {
  return supabase.auth.onAuthStateChange(callback)
}

export default supabase
